import React, { useState, useEffect } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { API_BASE } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  BookX, RefreshCw, CheckCircle2, XCircle, Loader2,
  Brain, Target, TrendingUp, AlertCircle, Trash2,
  ChevronRight, RotateCcw
} from 'lucide-react';

const MistakeTrackerPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [mistakes, setMistakes] = useState([]);
  const [stats, setStats] = useState(null);
  const [revisionQueue, setRevisionQueue] = useState([]);
  const [view, setView] = useState('bank'); // bank, revision, stats
  const [currentRevision, setCurrentRevision] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [revisionResult, setRevisionResult] = useState(null);
  const [subjectFilter, setSubjectFilter] = useState('all');

  useEffect(() => {
    fetchMistakes();
    fetchStats();
  }, []);

  const fetchMistakes = async () => {
    try {
      setLoading(true);
      const params = subjectFilter !== 'all' ? `?subject=${subjectFilter}` : '';
      const { data } = await axios.get(`${API_BASE}/mistakes/bank${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMistakes(data.mistakes || []);
    } catch (err) {
      console.error('Error fetching mistakes:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/mistakes/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchRevisionQueue = async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/mistakes/revision-queue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRevisionQueue(data.queue || []);
      setCurrentRevision(0);
      setSelectedAnswer(null);
      setRevisionResult(null);
    } catch (err) {
      console.error('Error fetching revision queue:', err);
    }
  };

  const handleRevisionSubmit = async () => {
    if (selectedAnswer === null || !revisionQueue[currentRevision]) return;
    
    try {
      const { data } = await axios.post(
        `${API_BASE}/mistakes/revise`,
        {
          question_id: revisionQueue[currentRevision].question_id,
          selected_option: selectedAnswer
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRevisionResult(data);
    } catch (err) {
      console.error('Error submitting revision:', err);
    }
  };

  const handleNextRevision = () => {
    if (currentRevision < revisionQueue.length - 1) {
      setCurrentRevision(prev => prev + 1);
      setSelectedAnswer(null);
      setRevisionResult(null);
    } else {
      // Finished all revisions
      fetchStats();
      setView('bank');
      fetchMistakes();
    }
  };

  const handleRemoveMistake = async (questionId) => {
    try {
      await axios.delete(`${API_BASE}/mistakes/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMistakes(prev => prev.filter(m => m.question_id !== questionId));
      fetchStats();
    } catch (err) {
      console.error('Error removing mistake:', err);
    }
  };

  if (loading && view === 'bank') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-orange-50 p-4" data-testid="mistake-tracker-page">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <BookX className="w-7 h-7 text-rose-500" />
              Mistake Tracker
            </h1>
            <p className="text-slate-600">Learn from your mistakes, don't repeat them</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant={view === 'bank' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setView('bank')}
            >
              Mistake Bank
            </Button>
            <Button
              variant={view === 'revision' ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setView('revision'); fetchRevisionQueue(); }}
              className="bg-rose-500 hover:bg-rose-600"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Revise Now
            </Button>
          </div>
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <div className="bg-white rounded-lg p-3 shadow-sm text-center">
              <BookX className="w-5 h-5 text-rose-500 mx-auto" />
              <div className="text-xl font-bold">{stats.total_mistakes}</div>
              <div className="text-xs text-slate-500">Total Mistakes</div>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm text-center">
              <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />
              <div className="text-xl font-bold">{stats.mastered}</div>
              <div className="text-xs text-slate-500">Mastered</div>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm text-center">
              <AlertCircle className="w-5 h-5 text-amber-500 mx-auto" />
              <div className="text-xl font-bold">{stats.pending_revision}</div>
              <div className="text-xs text-slate-500">Need Revision</div>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm text-center">
              <TrendingUp className="w-5 h-5 text-blue-500 mx-auto" />
              <div className="text-xl font-bold">{stats.revision_accuracy}%</div>
              <div className="text-xs text-slate-500">Revision Accuracy</div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto">
        {/* Mistake Bank View */}
        {view === 'bank' && (
          <>
            {mistakes.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h2 className="text-xl font-bold mb-2">No Mistakes Yet!</h2>
                  <p className="text-slate-600">Complete MCQ challenges and practice tests. Wrong answers will appear here for revision.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Weak Areas */}
                {stats?.weak_areas?.length > 0 && (
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-amber-800 mb-2">📊 Weak Areas</h3>
                      <div className="flex flex-wrap gap-2">
                        {stats.weak_areas.map((area, idx) => (
                          <Badge 
                            key={idx} 
                            variant="outline"
                            className="cursor-pointer hover:bg-amber-100"
                            onClick={() => setSubjectFilter(area.subject)}
                          >
                            {area.subject}: {area.mistakes} mistakes
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Mistakes List */}
                {mistakes.map((mistake, idx) => (
                  <Card key={mistake.question_id || idx} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline">{mistake.subject || 'General'}</Badge>
                            <Badge variant="secondary">{mistake.source || 'Practice'}</Badge>
                            {mistake.wrong_count > 1 && (
                              <Badge className="bg-rose-500">Wrong {mistake.wrong_count}x</Badge>
                            )}
                            {mistake.mastered && (
                              <Badge className="bg-green-500">Mastered</Badge>
                            )}
                          </div>
                          <p className="font-medium text-slate-900">{mistake.question}</p>
                          
                          <div className="mt-3 space-y-1">
                            {mistake.options?.map((opt, optIdx) => (
                              <div 
                                key={optIdx}
                                className={`text-sm p-2 rounded ${
                                  optIdx === mistake.correct_option 
                                    ? 'bg-green-100 text-green-800' 
                                    : optIdx === mistake.user_answer
                                    ? 'bg-rose-100 text-rose-800'
                                    : 'text-slate-600'
                                }`}
                              >
                                {String.fromCharCode(65 + optIdx)}. {opt}
                                {optIdx === mistake.correct_option && ' ✓'}
                                {optIdx === mistake.user_answer && optIdx !== mistake.correct_option && ' (Your answer)'}
                              </div>
                            ))}
                          </div>
                          
                          {mistake.explanation && (
                            <p className="text-sm text-slate-600 mt-2 p-2 bg-slate-50 rounded">
                              💡 {mistake.explanation}
                            </p>
                          )}
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveMistake(mistake.question_id)}
                          className="text-slate-400 hover:text-rose-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Revision Mode */}
        {view === 'revision' && (
          <>
            {revisionQueue.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h2 className="text-xl font-bold mb-2">All Caught Up!</h2>
                  <p className="text-slate-600">No mistakes pending revision. Keep practicing!</p>
                  <Button className="mt-4" onClick={() => setView('bank')}>
                    Back to Bank
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Brain className="w-5 h-5 text-purple-500" />
                      Revision Mode
                    </CardTitle>
                    <Badge>{currentRevision + 1} / {revisionQueue.length}</Badge>
                  </div>
                  <Progress value={((currentRevision + 1) / revisionQueue.length) * 100} className="h-2" />
                </CardHeader>
                <CardContent className="p-6">
                  {revisionQueue[currentRevision] && (
                    <>
                      <p className="text-lg font-medium mb-4">
                        {revisionQueue[currentRevision].question}
                      </p>
                      
                      <div className="space-y-3">
                        {revisionQueue[currentRevision].options?.map((option, idx) => (
                          <button
                            key={idx}
                            onClick={() => !revisionResult && setSelectedAnswer(idx)}
                            disabled={revisionResult !== null}
                            className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                              revisionResult
                                ? idx === revisionQueue[currentRevision].correct_option
                                  ? 'border-green-500 bg-green-50'
                                  : idx === selectedAnswer
                                  ? 'border-rose-500 bg-rose-50'
                                  : 'border-slate-200'
                                : selectedAnswer === idx
                                ? 'border-indigo-500 bg-indigo-50'
                                : 'border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <span className="font-medium mr-2">{String.fromCharCode(65 + idx)}.</span>
                            {option}
                          </button>
                        ))}
                      </div>

                      {/* Result */}
                      {revisionResult && (
                        <div className={`mt-4 p-4 rounded-lg ${revisionResult.is_correct ? 'bg-green-50' : 'bg-rose-50'}`}>
                          <div className="flex items-center gap-2 mb-2">
                            {revisionResult.is_correct ? (
                              <CheckCircle2 className="w-5 h-5 text-green-500" />
                            ) : (
                              <XCircle className="w-5 h-5 text-rose-500" />
                            )}
                            <span className="font-semibold">
                              {revisionResult.message}
                            </span>
                          </div>
                          {revisionResult.explanation && (
                            <p className="text-sm text-slate-700">{revisionResult.explanation}</p>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex justify-between mt-6">
                        <Button variant="outline" onClick={() => setView('bank')}>
                          Exit Revision
                        </Button>
                        
                        {!revisionResult ? (
                          <Button 
                            onClick={handleRevisionSubmit}
                            disabled={selectedAnswer === null}
                          >
                            Check Answer
                          </Button>
                        ) : (
                          <Button onClick={handleNextRevision}>
                            {currentRevision < revisionQueue.length - 1 ? (
                              <>Next <ChevronRight className="w-4 h-4 ml-1" /></>
                            ) : (
                              'Finish'
                            )}
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MistakeTrackerPage;
